import { createSelector } from "reselect";

// Module selectors
export const getViewManagementState = (state) => state['viewManagement'] || {};

const getConfigInfo = (state) => state && state.config;

// Drawer selectors
export const getIsDrawerOpen = (state) =>
  getViewManagementState(state)?.isDrawerOpen || false;

export const getCurrentView = (state) =>
  getViewManagementState(state)?.currentView || null;

export const getDefaultView = (state) =>
  getViewManagementState(state)?.defaultView || null;

// Dialog selectors
export const getIsDialogOpen = (state) =>
  getViewManagementState(state)?.isDialogOpen || false;

export const getDialogType = (state) =>
  getViewManagementState(state)?.dialogType || null;

export const getDialogSubtype = (state) =>
  getViewManagementState(state)?.dialogSubtype || null;

export const getDialogData = (state) =>
  getViewManagementState(state)?.dialogData || null;

// Views data selectors
export const getViews = (state) => getViewManagementState(state)?.views || [];

export const getViewsInfo = (state) => getViewManagementState(state)?.viewsInfo || null;

export const getViewById = (state, viewId) =>
  getViews(state).find((view) => view.id === viewId);

export const getViewByName = (state, viewName) =>
  getViews(state).find((view) => view.name === viewName);

// Loading state selectors
export const getViewManagementLoading = (state) =>
  getViewManagementState(state)?.loading || false;

export const getSaveLoading = (state) =>
  getViewManagementState(state)?.saveLoading || false;

export const getDeleteLoading = (state) =>
  getViewManagementState(state)?.deleteLoading || false;

export const getRenameLoading = (state) =>
  getViewManagementState(state)?.renameLoading || false;

export const getEditLoading = (state) =>
  getViewManagementState(state)?.editLoading || false;

export const getSelectLoading = (state) =>
  getViewManagementState(state)?.selectLoading || false;

export const getSetDefaultLoading = (state) =>
  getViewManagementState(state)?.setDefaultLoading || false;

export const getPrimaryListDataLoading = (state) =>
  getViewManagementState(state)?.primaryListDataLoading || false;

// Data selectors
export const getAvailableViews = (state) => getViews(state);

export const getSelectedViewForEdit = (state) =>
  getViewManagementState(state)?.selectedViewForEdit || null;

export const getSelectedViews = (state) =>
  getViewManagementState(state)?.selectedViews || [];

// Error selectors
export const getViewManagementError = (state) =>
  getViewManagementState(state)?.error || null;

export const getViewManagementAlerts = (state) =>
  getViewManagementState(state)?.alerts || [];

// List data selectors
export const getSiteList = (state) =>
  getViewManagementState(state)?.siteList || [];

export const getEditAccessSiteList = (state) =>
  (getViewManagementState(state)?.siteList || []).filter(
    (site) => site?.accessLevel === 'EDIT'
  );

export const getNoAccessSiteList = (state) =>
  getViewManagementState(state)?.noAccessSiteList || [];

export const getCostCenterList = (state) =>
  getViewManagementState(state)?.costCenterList || [];

export const getQuickLinkContactsList = (state) =>
  getViewManagementState(state)?.quickLinkContactsList || [];

export const getIsViewAndUserConfigReady = (state) =>
  getViewManagementState(state)?.isViewAndUserConfigReady || false;

// Combined list selectors
export const getAllListsInViewManagement = (state) => {
  const viewState = getViewManagementState(state);
  return {
    sites: viewState?.siteList || [],
    noAccessSites: viewState?.noAccessSiteList || [],
    costCenters: viewState?.costCenterList || [],
  };
};

export const getListItemById = (state, listType, itemId) => {
  const viewState = getViewManagementState(state);
  let list = [];

  switch (listType) {
    case 'site':
      list = viewState?.siteList || [];
      break;
    case 'noAccessSite':
      list = viewState?.noAccessSiteList || [];
      break;
    case 'costCenter':
      list = viewState?.costCenterList || [];
      break;
    default:
      return null;
  }

  return list.find((item) => item.id === itemId) || null;
};

export const getListsByAccess = (state) => {
  const viewState = getViewManagementState(state);
  const allItems = [
    ...(viewState?.siteList || []).map((item) => ({ ...item, type: 'site' })),
    ...(viewState?.noAccessSiteList || []).map((item) => ({ ...item, type: 'noAccessSite' })),
    ...(viewState?.costCenterList || []).map((item) => ({
      ...item,
      type: 'costCenter',
    })),
  ];

  return {
    withAccess: allItems.filter((item) => item.hasAccess),
    withoutAccess: allItems.filter((item) => !item.hasAccess),
  };
};

// Filtered list data selectors
export const getFilteredSiteListByViews = (state) =>
  getViewManagementState(state)?.filteredSiteListByViews || {};

export const getFilteredCostCenterListByViews = (state) =>
  getViewManagementState(state)?.filteredCostCenterListByViews || {};

// Default filtered list data selectors (for API provided data)
export const getDefaultFilteredSiteListByViews = (state) =>
  getViewManagementState(state)?.defaultFilteredSiteListByViews || {};

export const getDefaultFilteredCostCenterListByViews = (state) =>
  getViewManagementState(state)?.defaultFilteredCostCenterListByViews || {};

// Get default filtered lists for specific view
export const getDefaultFilteredSiteList = (state, viewName) => {
  const defaultFilteredByViews = getDefaultFilteredSiteListByViews(state);
  return defaultFilteredByViews[viewName] || [];
};

export const getDefaultFilteredCostCenterList = (state, viewName) => {
  const defaultFilteredByViews = getDefaultFilteredCostCenterListByViews(state);
  return defaultFilteredByViews[viewName] || [];
};

// Get default filtered lists for current view
export const getCurrentViewDefaultFilteredSiteList = (state) => {
  const currentView = getCurrentView(state);
  return getDefaultFilteredSiteList(state, currentView);
};

export const getCurrentViewDefaultFilteredCostCenterList = (state) => {
  const currentView = getCurrentView(state);
  return getDefaultFilteredCostCenterList(state, currentView);
};

export const getCurrentViewAllDefaultFilteredLists = (state) => {
  const currentView = getCurrentView(state);
  return {
    sites: getDefaultFilteredSiteList(state, currentView),
    costCenters: getDefaultFilteredCostCenterList(state, currentView),
  };
};

export const hasModifiedFiltersSelector = (state) => {
  const currentView = getCurrentView(state);
  const allFilteredLists = {
    sites: getFilteredSiteList(state, currentView),
    costCenters: getFilteredCostCenterList(state, currentView),
  };
  const allDefaultFilteredLists = {
    sites: getDefaultFilteredSiteList(state, currentView),
    costCenters: getDefaultFilteredCostCenterList(state, currentView),
  };
  return Object.keys(allFilteredLists).some(key => {
    const filteredList = allFilteredLists[key];
    const defaultFilteredList = allDefaultFilteredLists[key];
    if (filteredList.length !== defaultFilteredList.length) {
      return true;
    }

    const id = key === 'sites' ? 'site' : 'costCenterId';

    const filteredIds = new Set(filteredList.map(item => item[id]));
    for (const item of defaultFilteredList) {
      if (!filteredIds.has(item[id])) {
        return true;
      }
    }
    return false;
  });
}

// Get filtered lists for specific view
export const getFilteredSiteList = (state, viewName) => {
  const filteredByViews = getFilteredSiteListByViews(state);
  return filteredByViews[viewName] || [];
};

export const getFilteredCostCenterList = (state, viewName) => {
  const filteredByViews = getFilteredCostCenterListByViews(state);
  return filteredByViews[viewName] || [];
};

// Get filtered lists for current view
export const getCurrentViewFilteredSiteList = (state) => {
  const currentView = getCurrentView(state);
  return getFilteredSiteList(state, currentView);
};

export const getCurrentViewFilteredCostCenterList = (state) => {
  const currentView = getCurrentView(state);
  return getFilteredCostCenterList(state, currentView);
};

// Combined filtered list selectors
export const getAllFilteredLists = (state) => {
  const viewName = getCurrentView(state);
  return {
    sites: getFilteredSiteList(state, viewName),
    costCenters: getFilteredCostCenterList(state, viewName),
  };
};

export const getCurrentViewAllFilteredLists = (state) => {
  const currentView = getCurrentView(state);
  return getAllFilteredLists(state, currentView);
};

const toUniqueStringArray = (values = []) => {
  const normalized = values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);
  return Array.from(new Set(normalized));
};

const mapSiteValue = (item) => item?.site || item?.siteName || '';
const mapCostCenterValue = (item) => item?.name || item?.description || item?.costCenterId || '';

export const getCurrentViewResolvedSiteAndCostCenter = createSelector(
  [
    getCurrentViewDefaultFilteredSiteList,
    getCurrentViewDefaultFilteredCostCenterList,
    getSiteList,
    getCostCenterList,
  ],
  (initialSites, initialCostCenters, allSites, allCostCenters) => {
    const hasSelectedSites = (initialSites || []).length > 0;
    const hasSelectedCostCenters = (initialCostCenters || []).length > 0;

    const sourceSites = hasSelectedSites ? initialSites : allSites;
    const sourceCostCenters = hasSelectedCostCenters
      ? initialCostCenters
      : allCostCenters;
    
    // Filter unique cost centers by name
    const uniqueCostCenters = [];
    const seenNames = new Set();
    (sourceCostCenters || []).forEach((cc) => {
      const name = mapCostCenterValue(cc);
      if (name && !seenNames.has(name)) {
        seenNames.add(name);
        uniqueCostCenters.push(cc);
      }
    });
    
    return {
      sites: toUniqueStringArray((sourceSites || []).map(mapSiteValue)),
      costCenters: toUniqueStringArray(
        (sourceCostCenters || []).map(mapCostCenterValue),
      ),
      viewCostCenterList: uniqueCostCenters,
      hasSelectedSites,
      hasSelectedCostCenters,
      hasAllSiteAccess: !hasSelectedSites,
      hasAllCostCenterAccess: !hasSelectedCostCenters,
    };
  },
);


export const getFilteredListsByAccess = (state, viewName) => {
  const allFilteredItems = [
    ...getFilteredSiteList(state, viewName).map((item) => ({
      ...item,
      type: 'site',
    })),
    ...getFilteredCostCenterList(state, viewName).map((item) => ({
      ...item,
      type: 'costCenter',
    })),
  ];

  return {
    withAccess: allFilteredItems.filter((item) => item.hasAccess),
    withoutAccess: allFilteredItems.filter((item) => !item.hasAccess),
  };
};

export const getFilteredListItemById = (state, listType, itemId, viewName) => {
  let list = [];

  switch (listType) {
    case 'site':
      list = getFilteredSiteList(state, viewName);
      break;
    case 'costCenter':
      list = getFilteredCostCenterList(state, viewName);
      break;
    default:
      return null;
  }

  return list.find((item) => item.id === itemId) || null;
};



// API URLs - to be configured based on your API endpoints
export const getViewManagementApiUrl = (state) => {
  const config = state.config;
  const baseUrl = config?.client?.flowApiUrl || '';
  return `${baseUrl}api/portfolio-views?`;
};

export const getViewManagementListApiUrl = (state) => {
  const config = state.config;
  const baseUrl = config?.client?.flowApiUrl || '';
  return `${baseUrl}api/lookup/system-default-view?`;
};

export const getSaveViewCount = (state) => {
  return getViewManagementState(state)?.saveViewCount || 0;
}

export const getUpdateViewCount = (state) => {
  return getViewManagementState(state)?.defaultViewUpdateCount || 0;
}

export const getDeleteViewCount = (state) => {
  return getViewManagementState(state)?.deleteViewCount || 0;
}

export const getRenameViewApiUrl = (viewId) => createSelector([getConfigInfo], (configInfo) => {
  const baseUrl = configInfo?.client?.flowApiUrl || '';
  return `${baseUrl}api/portfolio-views/${viewId}/rename?`;
});
export const getDeleteViewApiUrl = (viewId) => createSelector([getConfigInfo], (configInfo) => {
  const baseUrl = configInfo?.client?.flowApiUrl || '';
  return `${baseUrl}api/portfolio-views/${viewId}/?`;
});

export const getUpdateViewAction = (viewId) => createSelector([getConfigInfo], (configInfo) => {
  const baseUrl = configInfo?.client?.flowApiUrl || '';
  return `${baseUrl}api/portfolio-views/${viewId}/?`;
});

export const getSaveViewApiUrl = (state) => {
  const config = state.config;
  const baseUrl = config?.client?.flowApiUrl || '';
  return `${baseUrl}api/portfolio-views?`;
};

export const getCostCenterApiUrl = createSelector(
  [getConfigInfo],
  (configInfo) =>
    configInfo && configInfo.client && configInfo.client.flowApiUrl
      ? `${configInfo.client.flowApiUrl}api/lookups/get-cost-center?`
      : '',
);

export const getQuickLinkContactsApiUrl = createSelector(
  [getConfigInfo],
  (configInfo) =>
    configInfo && configInfo.client && configInfo.client.flowApiUrl
      ? `${configInfo.client.flowApiUrl}api/lookup/quick-links-contacts`
      : '',
);

export const getSetDefaultViewApiUrl = (viewId, clientCode) => createSelector(
  [getConfigInfo],
  (configInfo) =>
    configInfo && configInfo.client && configInfo.client.flowApiUrl
      ? `${configInfo.client.flowApiUrl}api/portfolio-views/${viewId}/set-default?clientCode=${clientCode}`
      : '',
);
